import { Box, Button, Grid, Stack, TextField, useTheme } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import NoteClient, { NoteErrors } from "../apiClients/NoteClient";
import { UpsertAction } from "../Enums";
import { Note, NoteAssociationType } from "../models/dtos";
import { useErrorDisplay } from '../components/ErrorDisplay';
import BusySpinner from "../components/BusySpinner";
import { IsEmpty } from "../Helpers";

export interface NoteUpsertComponentProps {
    mode: UpsertAction;
    associationId?: string;
    associationType?: NoteAssociationType;
    noteId?: string;
}

const NoteUpsertComponent = ({ mode, associationId, associationType, noteId }: NoteUpsertComponentProps) => {

    const newNote = {
        Id: '',
        AssociationId: '',
        AssociationType: '',
        SystemUserId: '',
        Title: '',
        Content: '',
        IsDeleted: false,
        LastUpdateDate: ''
    }

    const [ErrorDisplay, setError] = useErrorDisplay();
    const [busy, setBusy] = useState<boolean>(false);
    const initialized = useRef<boolean>();
    const theme = useTheme();

    const [note, setNote] = useState<Note>(null);
    const [dataHasChanged, setDataHasChanged] = useState<boolean>(false);

    const loadData = async () => {
        try {
            setBusy(true);

            switch (mode) {
                case UpsertAction.Create:
                    setNote(newNote);
                    break;
                case UpsertAction.Update:
                    let n = await new NoteClient().GetSingle(noteId);
                    setNote(n);
                    setDataHasChanged(false);
                    break;
                default:
                    break;
            }
        }
        catch (error) {
            setError(error, NoteErrors.GetSingle);
        }
        finally {
            initialized.current = true;
            setBusy(false);
        }
    }

    useEffect(() => {
        if (initialized.current)
            return;

        loadData();
    });

    const handleSubmit = async () => {
        try {
            setBusy(true);

            if (mode === UpsertAction.Create) {
                let createNote = {
                    AssociationId: associationId,
                    AssociationType: associationType,
                    Title: note.Title,
                    Content: note.Content
                }

                await new NoteClient().Create(createNote);
                setDataHasChanged(false);
            }

        } catch (error) {
            setError(error, NoteErrors.Create);
        }
        finally {
            setBusy(false);
        }
    };

    const handleInputChange = e => {
        const { name, value } = e.target
        setNote({ ...note, [name]: value })
        setDataHasChanged(true);
    };

    if (!initialized.current) {
        return <></>
    } else {
        return (
            <Box>
                <ErrorDisplay />
                <BusySpinner busy={busy}>
                    <Grid sx={{ border: 1, width: '100%' }}  container>
                    <Grid item lg={6} xs={12}>
                            <Stack sx={{ marginTop: theme.spacing(2), marginBottom: theme.spacing(3) }}>
                                <TextField label="Title" fullWidth
                                    name="Title"
                                    sx={{ marginBottom: theme.spacing(2) }}
                                    size="small"
                                    variant="outlined"
                                    error={IsEmpty(note?.Title)}
                                    value={note?.Title ?? ''}
                                    onChange={handleInputChange} />
                                <TextField label="Note" fullWidth
                                    name="Content"
                                    sx={{ marginBottom: theme.spacing(2) }}
                                    size="small"
                                    variant="outlined"
                                    error={IsEmpty(note?.Content)}
                                    value={note?.Content ?? ''}
                                    onChange={handleInputChange} />
                            </Stack>
                        </Grid>
                    </Grid>
                    <Grid container sx={{ flexDirection: 'row-reverse', width: '100%' }} lg={6} xs={12}>
                            <Button variant='outlined' disabled={!dataHasChanged} onClick={handleSubmit}>Update</Button>
                        </Grid>
                </BusySpinner>
            </Box>
        )
    }
};

export default NoteUpsertComponent;
